import { PeTypes, ProjectTypes } from '@platform/types';
import { Helpers } from '@platform/utils';
import moment from 'moment';
import { RuleGroupType, RuleType } from 'react-querybuilder';
import { v4 as uuidv4 } from 'uuid';
import { ChartSerie } from '../components/Analysis/ChartTypes';
import * as peApi from '../pe.api';

const SUPPORTED_SIZE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'];

const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const getDataForSeries = (
  series: PeTypes.ChartSeries[],
  preProcessedData: { [key: string]: number }
): ChartSerie[] => {
  return series.map((serie) => {
    const chartData: number[] = [];
    let total: number | undefined;

    if (serie.universe) {
      total = preProcessedData[serie.universe] as number;
    }
    serie.data.forEach((element: string) => {
      if (total != null && !serie.ignoreUniverse) {
        const value = preProcessedData[element] as number;
        const percent = (value / total) * 100;
        chartData.push(percent || 0);
      } else chartData.push(preProcessedData[element]);
    });

    return {
      total,
      name: serie.name,
      data: chartData,
      type: serie.type,
      colors: serie.colors,
    };
  });
};

export const getCategoriesForChart = (categories: string[], metadata: { [key: string]: string }): string[] => {
  const cats: string[] = [];
  categories.forEach((category: string) => {
    cats.push(metadata[category]);
  });
  return cats;
};

export const getChartTitle = (title: string, metadata: { [key: string]: string }): string => {
  return metadata[title];
};

export const getStep = (steps: PeTypes.NumberDefinitionSteps[] | undefined, value: number) => {
  if (steps == null) return null;

  for (const stepObj of steps) {
    const { min, max, color, label } = stepObj;
    if (value < 0) {
      if (value < min && value >= max) {
        return { color, label };
      }
    } else if (value > 0) {
      if (value > min && value <= max) {
        return { color, label };
      }
    } else if (value === 0 && min === 0 && max === 0) {
      return { color, label };
    }
  }

  return null;
};

export const getTimeFromNow = (dateTime: Date) => moment(new Date(dateTime).getTime()).fromNow();

export const hasText = (text: string) => {
  const element = document.createElement('span');
  element.innerHTML = text.trim();

  return !!element.innerText.trim().length;
};

export const toHumanReadableFileSize = (fileSizeInBytes: number) => {
  if (fileSizeInBytes === 0) {
    return '0 B';
  }
  const i = Math.floor(Math.log(fileSizeInBytes) / Math.log(1024));
  const decimals = i > 2 ? 2 : 0;
  return `${(fileSizeInBytes / 1024 ** i).toFixed(decimals)} ${SUPPORTED_SIZE_UNITS[i]}`;
};

export const validateEmail = (email: string) => {
  return emailRegex.test(email);
};

const removeDeletedDatasetsFromUserUploadedWorkingLayers = async (
  mapContext: ProjectTypes.MapContext
): Promise<ProjectTypes.MapContext> => {
  const transformed: ProjectTypes.MapContext = { ...mapContext };
  const datasetsInUse = getUserUploadedWorkingLayerDatasetIds(transformed.mapObjects);

  const availableDatasets: Record<string, PeTypes.Dataset> = (
    await Promise.allSettled(datasetsInUse.map((datasetId) => peApi.getDataset(datasetId)))
  ).reduce((prev, curr) => {
    if (curr.status === 'rejected') return prev;
    if (!curr.value) return prev;
    return { ...prev, [curr.value.id]: curr.value };
  }, {});

  const availableDatasetsIds = Object.keys(availableDatasets);

  transformed.mapObjects.forEach((mapObj) => {
    const workingLayers: ProjectTypes.AnyWorkingLayer[] = [];

    mapObj.styleSpec.workingLayers.forEach((workingLayer) => {
      if (
        workingLayer.source !== 'user-upload' ||
        availableDatasetsIds.includes((workingLayer as ProjectTypes.UserUploadedWorkingLayer).metadata.datasetId)
      ) {
        workingLayers.push(workingLayer);
      }
    });
    mapObj.styleSpec.workingLayers = [...workingLayers];
  });

  return transformed;
};

const removeInvalidRulesFromWorkingLayers = async (
  mapContext: ProjectTypes.MapContext,
  removeInvalidRules: boolean
): Promise<ProjectTypes.MapContext> => {
  const transformed: ProjectTypes.MapContext = { ...mapContext };
  const datasetsInUse = getUserUploadedWorkingLayerDatasetIds(transformed.mapObjects);

  // fetch published state of those datasets
  const publishedDatasetStates: Record<string, PeTypes.DatasetPublication> = (
    await Promise.allSettled(datasetsInUse.map((datasetId) => peApi.getDatasetLastPublishedState(datasetId)))
  ).reduce((prev, curr) => {
    if (curr.status === 'rejected') return prev;
    if (!curr.value) return prev;
    return { ...prev, [curr.value.datasetId]: curr.value };
  }, {});

  transformed.mapObjects.forEach((mapObj) => {
    mapObj.styleSpec.workingLayers
      .filter((x) => x.source === 'user-upload')
      .forEach((workingLayer: ProjectTypes.AnyWorkingLayer) => {
        const userUploadedLayer = workingLayer as ProjectTypes.UserUploadedWorkingLayer;

        if (userUploadedLayer.filter) {
          const { datasetId } = userUploadedLayer.metadata;
          // get published state of dataset used in this working layer
          const publishedFilterableColumns = publishedDatasetStates[datasetId]
            ? publishedDatasetStates[datasetId]?.columns
                ?.filter((column) => column.filterable)
                .map((column) => column.name)
            : [];

          const updatedRules = userUploadedLayer.filter.rules.reduce<(RuleGroupType | RuleType)[]>((acc, rule) => {
            const isValid = Helpers.checkIfValidRule(rule, publishedFilterableColumns);
            if (isValid || !removeInvalidRules) {
              acc.push(isValid ? rule : { ...rule, disabled: true });
            }
            return acc;
          }, []);

          workingLayer.filter = {
            ...userUploadedLayer.filter,
            rules: updatedRules,
          };
        }
      });
  });
  return transformed;
};

const removeInvalidColumnsFromUserUploadedWorkingLayersTooltips = async (mapContext: ProjectTypes.MapContext) => {
  const transformed: ProjectTypes.MapContext = { ...mapContext };
  const datasetsInUse = getUserUploadedWorkingLayerDatasetIds(transformed.mapObjects);

  // fetch published state of those datasets
  const publishedDatasetStates: Record<string, PeTypes.DatasetPublication> = (
    await Promise.allSettled(datasetsInUse.map((datasetId) => peApi.getDatasetLastPublishedState(datasetId)))
  ).reduce((prev, curr) => {
    if (curr.status === 'rejected') return prev;
    if (!curr.value) return prev;
    return { ...prev, [curr.value.datasetId]: curr.value };
  }, {});

  transformed.mapObjects.forEach((mapObj) => {
    mapObj.styleSpec.workingLayers
      .filter((x) => x.source === 'user-upload')
      .forEach((workingLayer) => {
        const { tooltip } = workingLayer;
        const { datasetId } = (workingLayer as ProjectTypes.UserUploadedWorkingLayer).metadata;

        // get published state of dataset used in this working layer
        const publishedDatasetColumnsIds = new Set(publishedDatasetStates[datasetId]?.columns.map((x) => x.id));

        // filter out invalid columns from tooltip content
        tooltip.content = tooltip.content.filter((obj) => publishedDatasetColumnsIds.has(obj.id));
      });
  });

  return transformed;
};

export const transformMapContext =
  (removeInvalidRules = false) =>
  async (mapContext: ProjectTypes.MapContext) => {
    let transformed = { ...mapContext };

    // 1. check if any of dataset is deleted
    transformed = await removeDeletedDatasetsFromUserUploadedWorkingLayers(transformed);
    // 2. validate all filter rules inside working layers
    transformed = await removeInvalidRulesFromWorkingLayers(transformed, removeInvalidRules);
    // 3. remove invalid columns from tooltip content
    transformed = await removeInvalidColumnsFromUserUploadedWorkingLayersTooltips(transformed);

    const testLayer: ProjectTypes.WorkingLayer<
      ProjectTypes.SocialExplorerWorkingLayerMetadata,
      ProjectTypes.SocialExplorerRuleGroupType
    > = {
      id: '012499fa-5121-4836-967d-298d29a5d403',
      source: 'social-explorer',
      name: 'Total Gender',
      description: 'Political Census',
      type: ProjectTypes.LayerType.POLYGON,
      visible: true,
      featureSelection: [],
      maskingSelection: {
        doMask: false,
        features: [],
      },
      variableSelection: [
        {
          variableId: '58a596a4-03c8-4a94-a4e7-81cc37dddc1b',
          tableId: '75f49d8d-4aea-4b87-8a3d-a243bfe8c82f',
        },
      ],
      metadata: {
        tiles: `${process.env.NX_SOCEX_TILES_SERVER}?x={x}&y={y}&z={z}&layers={layers}&columns={columns}&projection=EPSG-3857`,
        surveyName: 'ADV_2022_test',
        isUserSurvey: false,
        geoLayerId: '316249', // counties: 316307, states: 316249
        dataLayerId: '368812', // counties: 368813, states: 368812
        datasetAbbreviation: 'SE',
        identifiers: {
          row: {
            variableId: 'Geo_FIPS',
            datasetId: '368812',
          },
          name: {
            variableId: 'Geo_QName',
            datasetId: '368812',
          },
        },
      },
      // filter: {
      //   combinator: 'any',
      //   rules: [
      //     {
      //       operator: '>',
      //       value: {
      //         type: ProjectTypes.DataFilterType.PERCENTAGE,
      //         value: 12,
      //         surveyName: 'ADV_2022_test',
      //         datasetAbbreviation: 'SE',
      //         tableId: '5d5a388c-feac-48ec-979e-dd4d920c3746',
      //         universeVariableId: 'cc471f02-8e74-441b-bad3-dd17c1c02696',
      //       },
      //       field: '485d83c7-b521-40ff-bb16-fdf69b0f0dae',
      //     },
      //     {
      //       operator: '<',
      //       value: {
      //         type: ProjectTypes.DataFilterType.COUNT,
      //         value: 500000,
      //         surveyName: 'ADV_2022_test',
      //         datasetAbbreviation: 'SE',
      //         tableId: '4841aefd-8bdd-4381-ac3d-58f30bdb7449',
      //         universeVariableId: 'd78a33b1-e922-4e1d-8ad1-aabab0776e72',
      //       },
      //       field: '99b7b15a-cc2b-4250-908b-d6021ff3f4b0',
      //     },
      //   ],
      // },
      tooltip: {
        enabled: true,
        title: { id: 'title', variableId: 'Geo_QName', label: '' }, //Geo_QName
        action: 'click',
        content: [
          {
            id: uuidv4(),
            variableId: '42aa1e21-cdf5-40e6-a3fd-668e56779090',
            comparisonBase: {
              type: 'pct',
              base: {
                id: uuidv4(),
                variableId: '58a596a4-03c8-4a94-a4e7-81cc37dddc1b',
                label: 'Total Gender (Unregistered Voters)',
                format: '0,0',
              },
            },
            label: 'Female (Unregistered Voters)',
            format: '0,0.00%',
          },
        ],
      },
      rendering: {
        type: 'shaded', // | 'shade' | 'bubble';
        style: {
          colorPalette: {
            id: 'PE',
            type: 'sequential',
          },
          opacities: [0.9, 0.5],
          cutPointSets: [
            {
              insufficientData: {
                label: 'No data',
                color: '#C0C0C0',
              },
              cutPoints: [
                {
                  title: '< 104,523',
                  displayInLegend: true,
                  from: -1.7976931348623157e308,
                  to: 104523,
                  color: '#76a5af',
                },
                {
                  title: '145,481 to 176,294',
                  displayInLegend: true,
                  from: 145481,
                  to: 176294,
                  color: '#92b6a9',
                },
                {
                  title: '176,294 to 240,732',
                  displayInLegend: true,
                  from: 176294,
                  to: 240731.66667,
                  color: '#b2c8ab',
                },
                {
                  title: '240,732 to 319,369',
                  displayInLegend: true,
                  from: 240731.66667,
                  to: 319368.5,
                  color: '#dce0c8',
                },
                {
                  title: '319,369 to 357,306',
                  displayInLegend: true,
                  from: 319368.5,
                  to: 357305.5,
                  color: '#f3e3c6',
                },
                {
                  title: '357,306 to 512,232',
                  displayInLegend: true,
                  from: 357305.5,
                  to: 512232.33333,
                  color: '#f8d0a4',
                },
                {
                  title: '512,232 to 725,233',
                  to: 725232.5,
                  from: 512232.33333,
                  displayInLegend: true,
                  color: '#f8c087',
                },
                {
                  title: '> 725,233',
                  to: 1.7976931348623157e308,
                  from: 725232.5,
                  displayInLegend: true,
                  color: '#f6b26b',
                },
              ],
            },
          ],
        },
      },
    };

    // @ts-ignore
    delete transformed.mapObjects[0].styleSpec.dataLayer;
    // @ts-ignore
    delete transformed.mapObjects[0].styleSpec.geoSelection;
    // @ts-ignore
    delete transformed.mapObjects[0].styleSpec.geoFeatureSelection;

    transformed.mapObjects[0].styleSpec.workingLayers = [
      testLayer,
      // ...transformed.mapObjects[0].styleSpec.workingLayers,
    ];

    // console.log(JSON.stringify(transformed.mapObjects[0].styleSpec));

    return transformed;
  };

const getUserUploadedWorkingLayerDatasetIds = (mapObjects: ProjectTypes.MapContextObject[]) =>
  mapObjects.reduce(
    (prev: string[], curr) => [
      ...prev,
      ...curr.styleSpec.workingLayers
        .filter((x) => x.source === 'user-upload')
        .map((x) => (x as ProjectTypes.UserUploadedWorkingLayer).metadata.datasetId),
    ],
    []
  );
